import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCcw, Gift, Phone, Mail, Image as ImageIcon } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` });
const fmt = (v) => (v ? new Date(v).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const STATUS_TONE = { new: '#722F37', contacted: '#b45309', closed: '#6b7280' };
const OPTION_NAME = { upload: 'Uploaded their own design', designer: 'Work with our designer' };

const ContestGiftRequestsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contest/gift-requests`, { headers: authHeaders() });
      setItems(data.items || []);
    } catch (e) {
      toast({ title: 'Could not load gift requests', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contest/gift-requests/${id}`, { status }, { headers: authHeaders() });
      load();
    } catch (e) { toast({ title: 'Update failed', variant: 'destructive' }); }
  };

  const viewPhoto = async (id) => {
    try {
      const { data } = await axios.get(`${API}/contest/gift-requests/${id}/label-photo`, { headers: authHeaders(), responseType: 'blob' });
      window.open(URL.createObjectURL(data), '_blank');
    } catch (e) { toast({ title: 'Could not open photo', variant: 'destructive' }); }
  };

  return (
    <div className="space-y-4" data-testid="gift-requests-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Gift className="w-5 h-5" style={{ color: '#722F37' }} /> Holiday Gift Requests</h2>
          <p className="text-sm text-gray-500">Follow up by phone or in store to confirm details and collect payment.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="gift-refresh"><RefreshCcw className="w-4 h-4 mr-2" /> Refresh</Button>
      </div>

      {loading ? (
        <p className="text-gray-400 py-8 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-gray-400" data-testid="gift-empty">No gift requests yet. They'll appear here as visitors submit the form.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <Card key={r.id} data-testid={`gift-request-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{r.name}</span>
                      <Badge style={{ background: STATUS_TONE[r.status] || '#722F37' }}>{r.status}</Badge>
                      <Badge variant="secondary">{r.giftType}</Badge>
                      <Badge variant="outline">Gift bottles</Badge>
                    </div>
                    <div className="mt-1 text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {r.phone || '—'}</span>
                      {r.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {r.email}</span>}
                      {r.quantity && <span>Qty: {r.quantity}</span>}
                      {r.occasion && <span>Occasion: {r.occasion}</span>}
                      {r.preferredContact && <span>Prefers: {r.preferredContact}</span>}
                    </div>
                    {r.personalizationNotes && <p className="mt-2 text-sm text-gray-700 italic">"{r.personalizationNotes}"</p>}
                    {r.label && (
                      <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-gray-700" data-testid={`gift-label-${r.id}`}>
                        <div>
                          <span className="font-semibold" style={{ color: '#722F37' }}>Custom label</span>
                          <span className="ml-2">{OPTION_NAME[r.label.option] || 'Work with our designer'}</span>
                        </div>
                        {r.label.photo ? (
                          <Button size="sm" variant="outline" className="mt-2 h-7 flex" onClick={() => viewPhoto(r.id)} data-testid={`gift-view-photo-${r.id}`}>
                            <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> View photo
                          </Button>
                        ) : (
                          <div className="mt-1 text-gray-500">No photo uploaded</div>
                        )}
                      </div>
                    )}
                    <p className="mt-1 text-xs text-gray-400">{fmt(r.createdAt)}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {r.status !== 'contacted' && <Button size="sm" variant="outline" onClick={() => setStatus(r.id, 'contacted')} data-testid={`gift-contacted-${r.id}`}>Mark contacted</Button>}
                    {r.status !== 'closed' && <Button size="sm" variant="outline" onClick={() => setStatus(r.id, 'closed')} data-testid={`gift-closed-${r.id}`}>Close</Button>}
                    {r.status !== 'new' && <Button size="sm" variant="ghost" onClick={() => setStatus(r.id, 'new')}>Reopen</Button>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContestGiftRequestsTab;
